// src/pages/Agenda.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import GlassCard from '../components/UI/GlassCard';
import Modal from '../components/UI/Modal';
import { 
    Calendar, Plus, Clock, User, CheckCircle2, Scissors, AlertCircle, 
    ExternalLink, CalendarDays, Search, Trash2, XCircle, FileText, 
    ShoppingCart, Menu as MenuIcon, X as CloseIcon 
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/formatters';
import tippy from 'tippy.js';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';
import 'tippy.js/animations/scale.css';
import Swal from 'sweetalert2';

// Componente para evitar el warning de ref en React 19
const SafeTippy = ({ children, ...props }) => {
    const [ref, setRef] = useState(null);
    return (
        <>
            <Tippy {...props} reference={ref} />
            {React.cloneElement(React.Children.only(children), { ref: setRef })}
        </>
    );
};

const Agenda = () => {
    const { can } = useAuth();
    const calendarRef = useRef(null);
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Auxiliares
    const [clientes, setClientes] = useState([]);
    const [mascotas, setMascotas] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [estados, setEstados] = useState([]);
    const [filteredMascotas, setFilteredMascotas] = useState([]);
    
    const [formData, setFormData] = useState({
        cliente_id: '', mascota_id: '', servicio_id: '', empleado_id: '', fecha_hora: '', notas: '', estado_id: 1,
        // Campos para cliente nuevo
        es_nuevo_cliente: false,
        nuevo_cliente_nombre: '',
        nuevo_cliente_apellido: '',
        nuevo_cliente_telefono: '',
        nuevo_cliente_telefono: '',
        nuevo_mascota_nombre: ''
    });

    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');

    // Sincronizar fecha_hora cuando cambian date o time
    useEffect(() => {
        if (selectedDate && selectedTime) {
            setFormData(prev => ({ ...prev, fecha_hora: `${selectedDate}T${selectedTime}` }));
        }
    }, [selectedDate, selectedTime]);

    const [searchServicioSidebar, setSearchServicioSidebar] = useState('');
    const [searchDetailServicio, setSearchDetailServicio] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [showDrawer, setShowDrawer] = useState(false);

    // Responsive check
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            
            // Si cambia a móvil, forzar vista de día si está en semana
            if (mobile && calendarRef.current) {
                const api = calendarRef.current.getApi();
                if (api.view.type === 'timeGridWeek') {
                    api.changeView('timeGridDay');
                }
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [saving, setSaving] = useState(false);

    const [config, setConfig] = useState({
        businessHours: [
            { daysOfWeek: [1,2,3,4,5], startTime: '08:00', endTime: '20:00' },
            { daysOfWeek: [6], startTime: '09:00', endTime: '18:00' }
        ],
        slotMinTime: '07:00:00',
        slotMaxTime: '21:00:00'
    });

    const fetchData = useCallback(async () => {
        try {
            const [resEvents, resClientes, resMascotas, resServicios, resEmpleados, resEstados, resConfig] = await Promise.all([
                axios.get('/backend/api/citas/index.php'),
                axios.get('/backend/api/clientes/index.php'),
                axios.get('/backend/api/mascotas/index.php'),
                axios.get('/backend/api/servicios/index.php'),
                axios.get('/backend/api/empleados/index.php').catch(() => ({data: {success: false}})),
                axios.get('/backend/api/citas/estados.php'),
                axios.get('/backend/api/configuracion/index.php')
            ]);
            
            if (resConfig?.data?.success) {
                const rawJson = resConfig.data.data.business_hours_json;
                if (rawJson) {
                    const data = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
                    const daysMap = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
                    
                    const fcHours = [];
                    let minTime = '23:59';
                    let maxTime = '00:00';

                    Object.keys(data).forEach(key => {
                        const day = data[key];
                        if (day.active) {
                            fcHours.push({
                                daysOfWeek: [daysMap[key]],
                                startTime: day.open,
                                endTime: day.close
                            });
                            if (day.open < minTime) minTime = day.open;
                            if (day.close > maxTime) maxTime = day.close;
                        }
                    });

                    // Añadir margenes de 1 hora
                    let finalSlotMin = '07:00:00';
                    let finalSlotMax = '21:00:00';

                    if (minTime !== '23:59' && maxTime !== '00:00') {
                        const minHour = Math.max(0, parseInt(minTime.split(':')[0]) - 1);
                        const maxHour = Math.min(23, parseInt(maxTime.split(':')[0]) + 1);
                        finalSlotMin = `${String(minHour).padStart(2, '0')}:00:00`;
                        finalSlotMax = `${String(maxHour).padStart(2, '0')}:00:00`;
                    }

                    setConfig({
                        businessHours: fcHours,
                        slotMinTime: finalSlotMin,
                        slotMaxTime: finalSlotMax
                    });
                }
            }

            if (resEvents.data.success) {
                const formattedEvents = resEvents.data.data.map(e => ({
                    id: e.id,
                    title: `${e.mascota_nombre} | ${e.estado_nombre}`,
                    start: e.fecha_hora.replace(' ', 'T'),
                    allDay: false, // Forzar que sea un evento con hora
                    backgroundColor: e.estado_color || '#8b5cf6',
                    borderColor: 'rgba(255,255,255,0.2)',
                    textColor: '#fff',
                    extendedProps: { ...e }
                }));
                setEvents(formattedEvents);
            }
            if (resClientes.data.success) setClientes(resClientes.data.data);
            if (resMascotas.data.success) setMascotas(resMascotas.data.data);
            if (resServicios.data.success) setServicios(resServicios.data.data);
            if (resEmpleados.data.success) setEmpleados(resEmpleados.data.data);
            if (resEstados.data.success) setEstados(resEstados.data.data);
        } catch (error) {
            console.error('Error sincronizando agenda');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Inicializar Draggable para eventos externos
    useEffect(() => {
        if (!loading && servicios.length > 0) {
            const draggableEl = document.getElementById('external-events');
            if (draggableEl) {
                new Draggable(draggableEl, {
                    itemSelector: '.fc-external-event',
                    eventData: function(eventEl) {
                        return {
                            title: eventEl.getAttribute('data-title'),
                            duration: '01:00',
                            extendedProps: {
                                servicio_id: eventEl.getAttribute('data-id'),
                                isExternal: true
                            }
                        };
                    }
                });
            }
        }
    }, [loading, servicios]);

    // Carga de mascotas inteligente con auto-selección
    useEffect(() => {
        if (formData.cliente_id) {
            // 1. Filtrar desde el pool global cargado en fetchData (para rapidez)
            const local = mascotas.filter(m => Number(m.cliente_id) === Number(formData.cliente_id));
            
            // Eliminar duplicados lógicos (por nombre + especie)
            const uniqueLocalMap = new Map();
            local.forEach(m => {
                const key = `${m.nombre.toLowerCase()}-${m.especie_id}`;
                if (!uniqueLocalMap.has(key)) uniqueLocalMap.set(key, m);
            });
            const uniqueLocal = Array.from(uniqueLocalMap.values());
            setFilteredMascotas(uniqueLocal);

            // Auto-seleccionar la primera mascota si existe en local
            if (uniqueLocal.length > 0) {
                setFormData(prev => ({ ...prev, mascota_id: uniqueLocal[0].id }));
            }

            // 2. Si el pool está vacío, consultar API específica del cliente
            if (uniqueLocal.length === 0) {
                axios.get(`/backend/api/mascotas/index.php?cliente_id=${formData.cliente_id}`)
                    .then(res => {
                        if (res.data.success) {
                            const pets = res.data.data;
                            const uniquePetsMap = new Map();
                            pets.forEach(m => {
                                const key = `${m.nombre.toLowerCase()}-${m.especie_id}`;
                                if (!uniquePetsMap.has(key)) uniquePetsMap.set(key, m);
                            });
                            const uniquePets = Array.from(uniquePetsMap.values());
                            
                            setFilteredMascotas(uniquePets);
                            
                            // Auto-seleccionar la primera mascota si existe en respuesta API
                            if (uniquePets.length > 0) {
                                setFormData(prev => ({ ...prev, mascota_id: uniquePets[0].id }));
                            }
                        }
                    })
                    .catch(err => console.error("Error cargando mascotas:", err));
            }
        } else {
            setFilteredMascotas([]);
        }
    }, [formData.cliente_id, mascotas]);

    const handleEventClick = (info) => {
        setSelectedEvent(info.event.extendedProps);
        setShowDetailModal(true);
    };



    const handleSave = async (e) => {
        e.preventDefault();
        
        const now = new Date();
        const selected = new Date(formData.fecha_hora);
        if (selected < now) {
            return toast.error('No es posible agendar en el pasado. Mire hacia adelante 🐾');
        }

        setSaving(true);
        try {
            let finalClientId = formData.cliente_id;
            let finalMascotaId = formData.mascota_id;

            // Lógica de Registro Rápido
            if (formData.es_nuevo_cliente) {
                if (!formData.nuevo_cliente_nombre || !formData.nuevo_cliente_telefono || !formData.nuevo_mascota_nombre) {
                    return toast.error('Datos de cliente/mascota incompletos');
                }

                // 1. Crear Cliente
                const resCliente = await axios.post('/backend/api/clientes/index.php', {
                    nombre: formData.nuevo_cliente_nombre,
                    apellido: formData.nuevo_cliente_apellido,
                    telefono: formData.nuevo_cliente_telefono
                });

                if (resCliente.data.success) {
                    finalClientId = resCliente.data.data.id;
                    
                    // 2. Crear Mascota
                    const resMascota = await axios.post('/backend/api/mascotas/index.php', {
                        nombre: formData.nuevo_mascota_nombre,
                        cliente_id: finalClientId,
                        especie_id: 1 // Perro por defecto
                    });

                    if (resMascota.data.success) {
                        finalMascotaId = resMascota.data.data.id;
                    } else throw new Error("Error al crear mascota");
                } else throw new Error("Error al crear cliente");
            }

            if (!finalClientId || !finalMascotaId) return toast.error('Selecciona cliente y mascota');

            const payload = {
                ...formData,
                cliente_id: finalClientId,
                mascota_id: finalMascotaId
            };

            const response = await axios.post('/backend/api/citas/index.php', payload);
            if (response.data.success) {
                setShowModal(false);
                setFormData({
                    cliente_id: '', mascota_id: '', servicio_id: '', empleado_id: '', fecha_hora: '', notas: '', estado_id: 1,
                    es_nuevo_cliente: false, nuevo_cliente_nombre: '', nuevo_cliente_apellido: '', nuevo_cliente_telefono: '', nuevo_mascota_nombre: ''
                });
                setSelectedDate('');
                setSelectedTime('');
                fetchData();
                toast.success('Cita y cliente registrados con éxito 🦴');
            }
        } catch (error) {
            toast.error(error.message || 'Error al procesar registro');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateStatus = async (newStatusId) => {
        try {
            // Enviamos solo el nuevo estado para evitar conflictos con datos extra
            const response = await axios.put(`/backend/api/citas/index.php?id=${selectedEvent.id}`, {
                estado_id: newStatusId
            });
            if (response.data.success) {
                setShowDetailModal(false);
                fetchData();
                toast.success('Estado actualizado 🔄');
            }
        } catch (error) {
            console.error('Error al actualizar estado:', error);
            toast.error(error.response?.data?.message || 'Error al actualizar estado');
        }
    };

    const handleEventDrop = async (info) => {
        const { event } = info;
        const newDate = event.start;
        const now = new Date();

        if (newDate < now) {
            toast.error('No es posible mover citas al pasado 🐾');
            info.revert();
            return;
        }
        
        // Formatear fecha para el backend (YYYY-MM-DD HH:mm:ss)
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, '0');
        const day = String(newDate.getDate()).padStart(2, '0');
        const hours = String(newDate.getHours()).padStart(2, '0');
        const minutes = String(newDate.getMinutes()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:00`;

        try {
            const response = await axios.put(`/backend/api/citas/index.php?id=${event.id}`, {
                fecha_hora: formattedDate
            });

            if (response.data.success) {
                toast.success(`Cita de ${event.extendedProps.mascota_nombre} reprogramada 📅`);
                fetchData();
            }
        } catch (error) {
            info.revert();
            toast.error(error.response?.data?.message || 'Error al mover cita');
        }
    };

    const handleEventResize = async (info) => {
        const { event } = info;
        // Actualmente el backend no maneja duración/fin, pero podemos mostrar un aviso
        // O simplemente revertir si no está implementado
        toast.error('La duración fija está predefinida por el servicio. Use mover para reprogramar 🐾');
        info.revert();
    };

    const handleEventDidMount = (info) => {
        const { event } = info;
        const props = event.extendedProps;
        
        // No mostrar tooltip para eventos externos que se están arrastrando o si no hay datos básicos
        if (props.isExternal || !props.mascota_nombre) return;

        tippy(info.el, {
            content: `
                <div class="p-5 min-w-[260px] relative overflow-hidden group">
                    <!-- Background Glow Interior -->
                    <div class="absolute -right-10 -top-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    <div class="flex justify-between items-start mb-5 relative z-10">
                        <div class="flex flex-col">
                            <div class="flex items-center gap-2 mb-1">
                                <div class="w-2.5 h-2.5 rounded-full pulse-ring" style="background-color: ${event.backgroundColor || '#8b5cf6'}"></div>
                                <span class="text-[10px] font-black uppercase text-white/40 tracking-[2px] leading-none">${props.estado_nombre || 'Pendiente'}</span>
                            </div>
                            <h3 class="text-xl font-black text-white leading-tight tracking-tight">${props.mascota_nombre || 'Mascota'}</h3>
                        </div>
                        <!-- Mini Pill with Price -->
                        <div class="bg-white/5 border border-white/10 px-3 py-1.5 rounded-2xl backdrop-blur-md shadow-lg">
                            <span class="text-xs font-black text-emerald-400 font-mono tracking-tighter">${formatCurrency(props.servicio_precio || 0)}</span>
                        </div>
                    </div>

                    <div class="space-y-4 relative z-10">
                        <div class="flex flex-col gap-1">
                            <span class="text-[9px] font-black uppercase text-secondary/60 tracking-[1.5px]">Servicio Seleccionado</span>
                            <p class="text-xs font-bold text-white/90 italic drop-shadow-sm">${props.servicio_nombre || 'Servicio General'}</p>
                        </div>
                        
                        <div class="grid grid-cols-1 gap-3 border-t border-white/5 pt-4 mt-1">
                            <div class="flex items-center gap-3 group/item">
                                <div class="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center border border-white/5 transition-all group-hover/item:border-white/20">
                                    <span class="text-lg">👤</span>
                                </div>
                                <div class="flex flex-col">
                                    <span class="text-[9px] font-black uppercase text-white/20 tracking-wider">Responsable</span>
                                    <span class="text-[11px] font-extrabold text-white/80">${props.cliente_nombre || 'Cliente'} ${props.cliente_apellido || ''}</span>
                                </div>
                            </div>
                            ${props.groomer_nombre ? `
                            <div class="flex items-center gap-3 group/item">
                                <div class="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center border border-white/5 transition-all group-hover/item:border-white/20">
                                    <span class="text-lg">✂️</span>
                                </div>
                                <div class="flex flex-col">
                                    <span class="text-[9px] font-black uppercase text-white/20 tracking-wider">Especialista</span>
                                    <span class="text-[11px] font-extrabold text-white/80">${props.groomer_nombre}</span>
                                </div>
                            </div>` : ''}
                        </div>
                    </div>
                    
                    <!-- Bottom Gradient Visual Accent -->
                    <div class="absolute bottom-0 left-0 right-0 h-1 mt-auto" style="background: linear-gradient(90deg, transparent, ${event.backgroundColor || '#8b5cf6'}, transparent)"></div>
                </div>
            `,
            allowHTML: true,
            animation: 'scale',
            theme: 'glass-premium',
            placement: 'top',
            interactive: true,
            appendTo: () => document.body,
            delay: [150, 0]
        });
    };

    const goToBilling = () => {
        const servicesForBilling = (selectedEvent.servicios || []).map(s => ({
            id: s.servicio_id || s.id,
            descripcion: s.nombre,
            precio_unitario: s.precio_base || s.precio_aplicado,
            cantidad: 1
        }));

        navigate('/facturacion', { 
            state: { 
                prefillClienteId: selectedEvent.cliente_id, 
                cita_id: selectedEvent.id,
                items: servicesForBilling
            } 
        });
    };

    const renderEventContent = (eventInfo) => {
        const props = eventInfo.event.extendedProps;
        const isMonthView = eventInfo.view.type.includes('dayGrid');
        const isMirror = eventInfo.isMirror || eventInfo.isDragging;

        // Si es un evento externo que se está arrastrando por primera vez
        if (props.isExternal) {
            return (
                <div className="p-1.5 flex items-center justify-center h-full bg-secondary/20 rounded-lg">
                    <div className="text-[11px] font-extrabold uppercase text-white truncate">{eventInfo.event.title}</div>
                </div>
            );
        }

        if (isMonthView) {
            return (
                <div className={`flex items-center gap-2 px-2 py-1 w-full overflow-hidden ${isMirror ? 'bg-primary/20 ring-1 ring-primary-light' : ''}`}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0 shadow-lg" style={{ backgroundColor: eventInfo.event.backgroundColor }}></div>
                    <span className="text-[11px] font-black uppercase text-white truncate">
                        {props.mascota_nombre}
                    </span>
                    {props.facturada && (
                        <ShoppingCart size={10} className="text-emerald-400 flex-shrink-0" strokeWidth={3} />
                    )}
                    <span className="text-[10px] text-white/50 truncate font-semibold">
                        • {props.servicio_nombre}
                    </span>
                </div>
            );
        }

        return (
            <div className={`flex flex-col h-full overflow-hidden p-1.5 ${isMirror ? 'bg-primary/20 ring-1 ring-primary-light' : ''}`}>
                <div className="flex items-center justify-between gap-1 mb-0.5">
                    <span className="text-[11px] font-black uppercase text-white truncate leading-none">
                        {props.mascota_nombre}
                    </span>
                    {props.facturada ? (
                        <div className="bg-emerald-500/20 text-emerald-400 p-0.5 rounded-md border border-emerald-500/30 flex items-center justify-center animate-pulse" title="Pago Confirmado">
                            <ShoppingCart size={8} strokeWidth={4} />
                        </div>
                    ) : (
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: eventInfo.event.backgroundColor }}></div>
                    )}
                </div>
                {props.servicio_nombre && (
                    <span className="text-[9px] text-white/40 font-bold truncate leading-tight">
                        {props.servicio_nombre}
                    </span>
                )}
            </div>
        );
    };

    const handleEventReceive = (info) => {
        const { event } = info;
        const now = new Date();
        const dropDate = event.start;

        if (dropDate < now) {
            toast.error('No puedes agendar servicios en el pasado 🕰️');
            info.revert();
            return;
        }

        const servicioId = event.extendedProps.servicio_id;
        
        // Formatear manualmente para asegurar compatibilidad con datetime-local
        const year = dropDate.getFullYear();
        const month = String(dropDate.getMonth() + 1).padStart(2, '0');
        const day = String(dropDate.getDate()).padStart(2, '0');
        const hours = String(dropDate.getHours()).padStart(2, '0');
        const minutes = String(dropDate.getMinutes()).padStart(2, '0');
        const formattedForInput = `${year}-${month}-${day}T${hours}:${minutes}`;

        setFormData(prev => ({
            ...prev,
            servicio_id: servicioId,
            fecha_hora: formattedForInput,
            cliente_id: '',
            mascota_id: '',
            notas: '',
            es_nuevo_cliente: false
        }));

        setSelectedDate(`${year}-${month}-${day}`);
        setSelectedTime(`${hours}:${minutes}`);

        // Revertir para que no se quede el evento visualmente hasta que se guarde el formulario real
        info.revert();
        setTimeout(() => setShowModal(true), 10); // Pequeño delay para que el calendario termine de limpiar
    };

    if (loading) return (
        <div className="h-screen w-full flex items-center justify-center p-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
    );

    // Obtener fecha actual para el atributo min de los inputs
    const minDateTime = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

    return (
        <div className="page-enter p-8 lg:p-12 max-w-[1600px] mx-auto min-h-screen">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl lg:text-5xl font-extrabold flex items-center gap-3">
                        <CalendarDays className="text-secondary" size={42} /> 
                        Centro de <span className="text-secondary font-medium italic">Turnos</span>
                    </h1>
                    <p className="text-white/50 mt-2 text-sm lg:text-base font-medium flex items-center gap-2">
                        <Scissors size={14} className="text-primary-light" />
                        Gestión inteligente de la carga operativa de hoy
                    </p>
                </div>
                
                <div className="flex flex-wrap items-center gap-4">
                    <SafeTippy content="Descargar PDF de las citas de hoy o el día seleccionado" theme="glass-premium" animation="scale">
                        <button 
                            onClick={() => {
                                // Helper para formatear fecha local YYYY-MM-DD
                                const toLocalISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
                                
                                const currentApi = calendarRef.current?.getApi();
                                const currentDate = currentApi ? toLocalISO(currentApi.getDate()) : toLocalISO(new Date());
                                
                                const token = localStorage.getItem('token');
                                window.open(`/backend/api/reportes/agenda_pdf.php?fecha=${currentDate}&token=${token}`, '_blank');
                            }}
                            className="glass px-6 py-3.5 rounded-2xl flex items-center gap-3 text-white hover:bg-white/10 transition-all border border-white/10 active:scale-95 group shadow-xl"
                        >
                            <FileText size={20} className="text-secondary group-hover:scale-110 transition-transform" />
                            <span className="text-[14px] font-bold">REPORTE PDF</span>
                        </button>
                    </SafeTippy>

                    {isMobile && (
                        <SafeTippy content="Ver leyenda y servicios rápidos" theme="glass-premium">
                            <button 
                                onClick={() => setShowDrawer(true)}
                                className="glass p-3.5 rounded-2xl text-secondary hover:bg-white/10 transition-all border border-white/10 active:scale-95 shadow-xl"
                            >
                                <MenuIcon size={20} />
                            </button>
                        </SafeTippy>
                    )}

                    {can('Agenda', 'crear') && (
                        <SafeTippy content="Presiona para abrir el panel de agendamiento manual" theme="glass-premium" animation="scale">
                            <button 
                                onClick={() => setShowModal(true)}
                                className="premium-btn-primary flex items-center gap-3 px-8 h-[52px] group shadow-lg shadow-primary/20"
                            >
                                <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" />
                                <span className="text-[14px] font-bold uppercase tracking-tight">Nueva Cita</span>
                            </button>
                        </SafeTippy>
                    )}
                </div>
            </header>

            <div className="flex flex-col lg:flex-row gap-8">
                {/* SIDEBAR / DRAWER */}
                {(!isMobile || showDrawer) && (
                    <>
                        {/* Overlay para móvil */}
                        {isMobile && (
                            <div 
                                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity duration-300"
                                onClick={() => setShowDrawer(false)}
                            ></div>
                        )}

                        <aside 
                            className={`
                                ${isMobile ? 'fixed right-0 top-0 h-full w-[85%] max-w-[320px] z-[101] animate-in slide-in-from-right duration-500 p-6' : 'lg:w-80 space-y-6'}
                                ${isMobile ? 'glass border-l border-white/10 shadow-2xl overflow-y-auto' : ''}
                            `}
                        >
                            {isMobile && (
                                <div className="flex justify-between items-center mb-8">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 rounded-lg bg-secondary/20 flex items-center justify-center">
                                            <Plus className="text-secondary" size={16} />
                                        </div>
                                        <h2 className="text-sm font-black uppercase tracking-[2px] text-white">Opciones</h2>
                                    </div>
                                    <button onClick={() => setShowDrawer(false)} className="p-2 rounded-xl bg-white/5 text-white/40 hover:text-white">
                                        <CloseIcon size={20} />
                                    </button>
                                </div>
                            )}

                            <GlassCard padding="24px" className={`${isMobile ? 'bg-transparent border-none p-0 !shadow-none' : 'border-white/5'}`}>
                                <div className="flex items-center gap-2 mb-6">
                                    <Plus className="text-secondary" size={20} />
                                    <h2 className="text-lg font-black uppercase tracking-tighter">Servicios Rápidos</h2>
                                </div>
                                
                                <div className="relative mb-6">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" size={14} />
                                    <input 
                                        type="text" 
                                        placeholder="Buscar..." 
                                        value={searchServicioSidebar}
                                        onChange={(e) => setSearchServicioSidebar(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-xs text-white placeholder:text-white/20 focus:border-primary-light/50 outline-none transition-all"
                                    />
                                </div>

                                <div id="external-events" className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                                    {servicios
                                        .filter(s => s.nombre.toLowerCase().includes(searchServicioSidebar.toLowerCase()))
                                        .map(s => (
                                        <SafeTippy 
                                            key={s.id} 
                                            content={`Arrastra "${s.nombre}" al calendario para agendar rápidamente`} 
                                            placement={isMobile ? "left" : "right"} 
                                            theme="glass-premium"
                                        >
                                            <div 
                                                data-id={s.id}
                                                data-title={s.nombre}
                                                className="fc-external-event glass p-4 rounded-2xl cursor-grab active:cursor-grabbing hover:bg-white/5 transition-all group border border-white/5 overflow-hidden"
                                                onClick={() => isMobile && setShowDrawer(false)} // En móvil cerramos al tocar si es necesario o manejamos el drop
                                            >
                                                <div className="flex justify-between items-start">
                                                    <span className="text-xs font-bold text-white group-hover:text-secondary transition-colors">{s.nombre}</span>
                                                    <span className="text-[10px] font-black text-white/30">{formatCurrency(s.precio_base)}</span>
                                                </div>
                                                <p className="text-[10px] text-white/40 mt-1 line-clamp-1 italic">{s.categoria_nombre}</p>
                                            </div>
                                        </SafeTippy>
                                    ))}
                                </div>

                                <div className="mt-8 pt-6 border-t border-white/5">
                                    <div className="flex items-center gap-2 text-primary-light mb-4 text-[10px] font-black uppercase tracking-widest">
                                        <AlertCircle size={14} />
                                        <span>Leyenda de Estados</span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-3">
                                        {estados.map(est => (
                                            <div key={est.id} className="flex items-center gap-3">
                                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: est.color, boxShadow: `0 0 10px ${est.color}40` }}></div>
                                                <span className="text-[9px] font-black text-white/50 uppercase tracking-widest leading-none">{est.nombre}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </GlassCard>
                        </aside>
                    </>
                )}

                <div className="flex-1">
                    <GlassCard padding={isMobile ? "12px" : "30px"} className="shadow-2xl border-white/5 min-h-[700px] relative">
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            initialView={isMobile ? "timeGridDay" : "timeGridWeek"}
                            headerToolbar={isMobile ? {
                                left: 'prev,next',
                                center: 'title',
                                right: 'today,timeGridDay'
                            } : {
                                left: 'prev,next today',
                                center: 'title',
                                right: 'dayGridMonth,timeGridWeek,timeGridDay'
                            }}
                            editable={can('Agenda', 'editar')}
                            droppable={true}
                            businessHours={config.businessHours}
                            eventDrop={handleEventDrop}
                            eventResize={handleEventResize}
                            eventReceive={handleEventReceive}
                            eventDidMount={handleEventDidMount}
                            eventContent={renderEventContent}
                            events={events}
                            eventClick={handleEventClick}

                            locale="es"
                            slotMinTime={config.slotMinTime}
                            slotMaxTime={config.slotMaxTime}
                            height={isMobile ? "650px" : "750px"}
                            nowIndicator={true}
                            buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
                        />
                    </GlassCard>
                </div>
            </div>

            {/* MODAL CREAR CITA */}
            <Modal show={showModal} onClose={() => setShowModal(false)} title={formData.es_nuevo_cliente ? "Registro y Cita Express" : "Pautar Nueva Cita"} size="md">
                <form className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 pt-2">
                    
                    <div className="md:col-span-2 flex justify-between items-center bg-white/5 p-3 rounded-2xl border border-white/5 mb-2">
                        <span className="text-[11px] font-black uppercase text-white/50 tracking-widest">¿Es un cliente nuevo?</span>
                        <button 
                            type="button"
                            onClick={() => setFormData({...formData, es_nuevo_cliente: !formData.es_nuevo_cliente})}
                            className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase transition-all ${formData.es_nuevo_cliente ? 'bg-secondary text-white shadow-lg shadow-secondary/20' : 'bg-white/10 text-white/40'}`}
                        >
                            {formData.es_nuevo_cliente ? 'SÍ, ES NUEVO ✨' : 'NO, YA EXISTE'}
                        </button>
                    </div>

                    {!formData.es_nuevo_cliente ? (
                        <>
                            <div className="md:col-span-2 space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light italic">Propietario Responsable</label>
                                <select value={formData.cliente_id} onChange={e => setFormData({...formData, cliente_id: e.target.value})} className="input-field bg-slate-800/80">
                                    <option value="">Selecciona cliente...</option>
                                    {clientes.map(c => <option key={c.id} value={c.id} className="bg-slate-900">{c.nombre} {c.apellido}</option>)}
                                </select>
                            </div>

                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light italic">Mascota Paseante</label>
                                <select 
                                    value={formData.mascota_id} 
                                    onChange={e => setFormData({...formData, mascota_id: e.target.value})} 
                                    className="input-field bg-slate-800/80"
                                    disabled={!formData.cliente_id}
                                >
                                    <option value="">{formData.cliente_id ? 'Selecciona peludo...' : 'Primero selecciona un cliente'}</option>
                                    {filteredMascotas.map(m => (
                                        <option key={m.id} value={m.id}>{m.nombre} ({m.especie})</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-secondary italic">Nombre Dueño</label>
                                <input type="text" value={formData.nuevo_cliente_nombre} onChange={e => setFormData({...formData, nuevo_cliente_nombre: e.target.value})} className="input-field bg-slate-800/80" placeholder="Ej. Juan" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-secondary italic">Apellido</label>
                                <input type="text" value={formData.nuevo_cliente_apellido} onChange={e => setFormData({...formData, nuevo_cliente_apellido: e.target.value})} className="input-field bg-slate-800/80" placeholder="Ej. Pérez" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-secondary italic">Móvil Cliente</label>
                                <input type="text" value={formData.nuevo_cliente_telefono} onChange={e => setFormData({...formData, nuevo_cliente_telefono: e.target.value})} className="input-field bg-slate-800/80" placeholder="(787) 555-5555" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-bold uppercase tracking-widest text-secondary italic">🐶 Su Mascota</label>
                                <input type="text" value={formData.nuevo_mascota_nombre} onChange={e => setFormData({...formData, nuevo_mascota_nombre: e.target.value})} className="input-field bg-slate-800/80" placeholder="Nombre de mascota" />
                            </div>
                        </>
                    )}

                    <div className="border-t border-white/5 md:col-span-2 mt-2 pt-4"></div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light italic">Servicio</label>
                        <select value={formData.servicio_id} onChange={e => setFormData({...formData, servicio_id: e.target.value})} className="input-field bg-slate-800/80">
                            <option value="">Selecciona...</option>
                            {servicios.map(s => <option key={s.id} value={s.id} className="bg-slate-900">{s.nombre}</option>)}
                        </select>
                    </div>

                    <div className="space-y-3">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light italic flex items-center gap-2">
                            <Calendar size={12} /> Fecha Programada
                        </label>
                        <input 
                            type="date" 
                            min={minDateTime.split('T')[0]} 
                            value={selectedDate} 
                            onChange={e => setSelectedDate(e.target.value)} 
                            className="input-field bg-slate-800/80 [color-scheme:dark]" 
                        />
                    </div>

                    <div className="md:col-span-2 space-y-4 mt-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light italic flex items-center gap-2">
                            <Clock size={12} /> Seleccionar Horario Disponible
                        </label>
                        <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                            {(() => {
                                const slots = [];
                                for (let h = 8; h <= 18; h++) {
                                    const time = `${String(h).padStart(2, '0')}:00`;
                                    slots.push(time);
                                    if (h < 18) slots.push(`${String(h).padStart(2, '0')}:30`);
                                }
                                return slots.map(time => (
                                    <button
                                        key={time}
                                        type="button"
                                        onClick={() => setSelectedTime(time)}
                                        className={`py-3 rounded-xl text-xs font-bold transition-all border ${
                                            selectedTime === time 
                                                ? 'bg-primary text-white border-primary shadow-[0_0_15px_rgba(139,92,246,0.4)] scale-105' 
                                                : 'bg-white/5 text-white/40 border-white/5 hover:border-white/20 hover:bg-white/10'
                                        }`}
                                    >
                                        {time}
                                    </button>
                                ));
                            })()}
                        </div>
                    </div>

                    <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light italic">Especialista</label>
                        <select value={formData.empleado_id} onChange={e => setFormData({...formData, empleado_id: e.target.value})} className="input-field bg-slate-800/80">
                            <option value="">Cualquier disponible</option>
                            {empleados.map(e => <option key={e.id} value={e.id} className="bg-slate-900">{e.nombre}</option>)}
                        </select>
                    </div>

                    <div className="md:col-span-2 space-y-2">
                        <label className="text-[10px] font-bold uppercase tracking-widest text-primary-light italic">Instrucciones</label>
                        <textarea value={formData.notas} onChange={e => setFormData({...formData, notas: e.target.value})} className="input-field h-20 bg-slate-800/80 resize-none" placeholder="Cualquier detalle relevante..."></textarea>
                    </div>

                    <button 
                        onClick={handleSave} 
                        disabled={saving}
                        className={`md:col-span-2 premium-btn-primary py-4 font-black flex items-center justify-center gap-3 ${formData.es_nuevo_cliente ? 'bg-secondary' : ''} ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {saving ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                        ) : (
                            <>
                                {formData.es_nuevo_cliente ? <Plus size={20} /> : null}
                                {formData.es_nuevo_cliente ? 'REGISTRAR Y AGENDAR' : 'AGENDAR CITA'}
                            </>
                        )}
                    </button>
                </form>
            </Modal>

            {/* MODAL DETALLE */}
            <Modal 
                show={showDetailModal} 
                onClose={() => setShowDetailModal(false)} 
                title="Control de Turno" 
                size="md"
                footer={(
                    <div className="w-full flex flex-col gap-6">
                        {/* 🏷️ PANEL DE ACCIONES RÁPIDAS */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                {can('Agenda', 'editar') && selectedEvent?.estado_id < 4 && (
                                    <div className="flex items-center gap-3">
                                        <button 
                                            onClick={() => handleUpdateStatus(3)} 
                                            className="group flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500 hover:border-cyan-400 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all duration-300 text-[10px] font-black uppercase text-cyan-400 hover:text-white"
                                        >
                                            <Clock size={14} className="group-hover:rotate-12 transition-transform" /> EN PROCESO
                                        </button>
                                        <button 
                                            onClick={() => handleUpdateStatus(4)} 
                                            className="group flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500 hover:border-emerald-400 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-all duration-300 text-[10px] font-black uppercase text-emerald-400 hover:text-white"
                                        >
                                            <CheckCircle2 size={14} /> COMPLETADA
                                        </button>
                                        <button 
                                            onClick={async () => {
                                                const result = await Swal.fire({
                                                    title: '¿Cancelar Cita?',
                                                    text: "Este turno será marcado como cancelado y no se podrá procesar para cobro.",
                                                    icon: 'warning',
                                                    showCancelButton: true,
                                                    confirmButtonColor: '#ff4444',
                                                    cancelButtonColor: 'rgba(255,255,255,0.1)',
                                                    confirmButtonText: 'Sí, cancelar',
                                                    cancelButtonText: 'No, mantener',
                                                    background: 'rgba(15, 23, 42, 0.95)',
                                                    color: '#fff'
                                                });
                                                if (result.isConfirmed) handleUpdateStatus(5);
                                            }}
                                            className="group flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-rose-500/10 border border-rose-500/20 hover:bg-rose-500 hover:border-rose-400 hover:shadow-[0_0_20px_rgba(244,63,94,0.4)] transition-all duration-300 text-[10px] font-black uppercase text-rose-400 hover:text-white"
                                        >
                                            <XCircle size={14} /> CANCELAR
                                        </button>
                                    </div>
                                )}
                                {selectedEvent?.estado_id === 7 && (
                                    <div className="flex items-center gap-3 bg-emerald-500/5 text-emerald-400 px-6 py-3 rounded-2xl border border-emerald-500/10 animate-pulse">
                                        <CheckCircle2 size={16} />
                                        <span className="text-[11px] font-black uppercase tracking-[3px]">Cita Finalizada y Pagada</span>
                                    </div>
                                )}
                            </div>
                            <button 
                                onClick={() => setShowDetailModal(false)}
                                className="text-[10px] font-black uppercase text-white/20 hover:text-white hover:bg-white/5 py-3 px-5 rounded-2xl transition-all tracking-[2px]"
                            >
                                Cerrar Ventana
                            </button>
                        </div>

                        {selectedEvent?.estado_id === 4 && can('Ventas', 'crear') && (
                            <button 
                                onClick={goToBilling}
                                className="group relative w-full overflow-hidden rounded-[2rem] p-[2px] transition-all duration-500 hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-500 animate-gradient-x"></div>
                                <div className="relative flex items-center justify-center gap-4 bg-slate-900/90 py-5 rounded-[2rem] transition-colors group-hover:bg-transparent">
                                    <div className="bg-emerald-500 p-2 rounded-xl shadow-lg group-hover:bg-white group-hover:text-emerald-600 transition-all duration-500">
                                        <ShoppingCart size={20} className="text-white group-hover:text-inherit" />
                                    </div>
                                    <div className="flex flex-col items-start leading-none">
                                        <span className="text-[10px] font-black text-white/40 uppercase tracking-[3px] mb-1">Finalización de Turno</span>
                                        <span className="text-xl font-black text-white tracking-tighter">PROCEDER AL COBRO AHORA</span>
                                    </div>
                                    <ExternalLink size={20} className="text-emerald-400 group-hover:text-white transition-colors" />
                                </div>
                            </button>
                        )}

                        {selectedEvent?.estado_id === 7 && (
                            <div className="w-full bg-white/5 border border-white/5 p-6 rounded-[2rem] flex flex-col items-center gap-2">
                                <span className="text-[10px] font-black text-emerald-400 uppercase tracking-[4px]">Cita Pagada con Éxito</span>
                                <span className="text-[10px] text-white/30 italic">No se requieren acciones adicionales</span>
                            </div>
                        )}
                    </div>
                )}
            >
                {selectedEvent && (
                    <div className="space-y-8 pb-10">
                        <div className="flex items-center gap-6 bg-white/5 p-6 rounded-3xl border border-white/5">
                            <div className="w-20 h-20 rounded-2xl bg-slate-800 overflow-hidden border border-white/10 flex items-center justify-center text-3xl">
                                {selectedEvent.mascota_foto ? <img src={selectedEvent.mascota_foto} className="w-full h-full object-cover" /> : "🐾"}
                            </div>
                            <div>
                                <h3 className="text-2xl font-black text-white">{selectedEvent.mascota_nombre}</h3>
                                <p className="text-white/40 flex items-center gap-2 font-medium">
                                    <User size={14}/> {selectedEvent.cliente_nombre} {selectedEvent.cliente_apellido}
                                </p>
                                <span className="inline-block mt-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest" style={{ backgroundColor: selectedEvent.estado_color + '20', color: selectedEvent.estado_color, border: `1px solid ${selectedEvent.estado_color}40` }}>
                                    {selectedEvent.estado_nombre}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <h4 className="text-[10px] font-black uppercase tracking-[2px] text-white/30 flex items-center gap-2">
                                <Scissors size={12} className="text-primary-light" />
                                Servicios Contratados
                            </h4>
                            <div className="grid gap-2">
                                {(selectedEvent.servicios || []).map((s, idx) => (
                                    <div key={idx} className="flex justify-between items-center bg-white/5 p-3 rounded-2xl border border-white/5 group hover:border-white/10 transition-all">
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-white/90">{s.nombre}</span>
                                            <span className="text-[10px] text-white/30 italic">Precio base</span>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <span className="text-xs font-black text-emerald-400">{formatCurrency(s.precio_base || s.precio_aplicado)}</span>
                                            {can('Agenda', 'editar') && (
                                                <button 
                                                    onClick={async () => {
                                                        const currentServicios = (selectedEvent.servicios || []).map(sv => sv.servicio_id || sv.id);
                                                        const targetId = s.servicio_id || s.id;
                                                        const updatedList = currentServicios.filter(id => id !== targetId);
                                                        
                                                        try {
                                                            const res = await axios.put(`/backend/api/citas/index.php?id=${selectedEvent.id}`, {
                                                                servicios: updatedList
                                                            });
                                                            if (res.data.success) {
                                                                toast.success('Servicio removido 🗑️');
                                                                const newServices = updatedList.map(id => servicios.find(sv => sv.id === id));
                                                                setSelectedEvent(prev => ({ ...prev, servicios: newServices }));
                                                                fetchData();
                                                            }
                                                        } catch (err) {
                                                            toast.error('Error al remover servicio');
                                                        }
                                                    }}
                                                    className="p-1.5 rounded-lg bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500 hover:text-white"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                
                                {can('Agenda', 'editar') && (
                                    <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20" size={12} />
                                            <input 
                                                type="text" 
                                                placeholder="Buscar servicio para añadir..." 
                                                value={searchDetailServicio}
                                                onChange={(e) => setSearchDetailServicio(e.target.value)}
                                                className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-9 pr-4 text-[11px] text-white placeholder:text-white/20 focus:border-primary-light/50 outline-none transition-all"
                                            />
                                        </div>
                                        <select 
                                            onChange={async (e) => {
                                                const val = e.target.value;
                                                if (!val) return;
                                                const currentServicios = (selectedEvent.servicios || []).map(s => s.servicio_id || s.id);
                                                if (!currentServicios.includes(parseInt(val))) {
                                                    const updatedList = [...currentServicios, parseInt(val)];
                                                    try {
                                                        const res = await axios.put(`/backend/api/citas/index.php?id=${selectedEvent.id}`, {
                                                            servicios: updatedList
                                                        });
                                                        if (res.data.success) {
                                                            setSearchDetailServicio('');
                                                            toast.success('Servicio añadido con éxito ✂️');
                                                            const newServices = updatedList.map(id => servicios.find(sv => sv.id === id));
                                                            setSelectedEvent(prev => ({ ...prev, servicios: newServices }));
                                                            fetchData();
                                                        }
                                                    } catch (err) {
                                                        toast.error('Error al añadir servicio');
                                                    }
                                                }
                                            }}
                                            className="w-full bg-slate-900/50 border border-white/10 rounded-xl px-4 py-2.5 text-xs text-white focus:border-primary-light/50 outline-none transition-all cursor-pointer"
                                        >
                                            <option value="">{searchDetailServicio ? 'Resultados de búsqueda...' : '+ Añadir otro servicio...'}</option>
                                            {servicios
                                                .filter(s => {
                                                    const alreadyIn = (selectedEvent.servicios || []).some(es => (es.servicio_id || es.id) === s.id);
                                                    const matchesSearch = s.nombre.toLowerCase().includes(searchDetailServicio.toLowerCase());
                                                    return !alreadyIn && matchesSearch;
                                                })
                                                .map(s => <option key={s.id} value={s.id}>{s.nombre} - {formatCurrency(s.precio_base)}</option>)
                                            }
                                        </select>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>

            <style dangerouslySetInnerHTML={{ __html: `
                .fc { --fc-border-color: rgba(255,255,255,0.05); }
                .fc-toolbar-title { font-weight: 900 !important; color: white; }
                .fc-button { @apply !glass !border-none !rounded-xl !px-5 !py-2.5 !text-[10px] !font-black !uppercase !tracking-widest !text-white/60 hover:!text-white; }
                .fc-button-active { @apply !bg-primary-light !text-white !opacity-100; }
                .fc-event, .fc-event-mirror { 
                    border: none !important; 
                    border-left: 4px solid rgba(255,255,255,0.3) !important; 
                    padding: 0 !important;
                    border-radius: 10px !important; 
                    @apply shadow-2xl transition-all; 
                    background: rgba(255, 255, 255, 0.03) !important;
                    backdrop-filter: blur(10px);
                }
                .fc-daygrid-event { 
                    @apply !py-1 !px-0 !border-none !bg-white/5 hover:!bg-white/10 !rounded-xl !mb-1 !overflow-hidden; 
                    min-height: 28px !important;
                    display: flex !important;
                    align-items: center !important;
                }
                .fc-event-mirror {
                    opacity: 0.8;
                    z-index: 9999 !important;
                    @apply scale-105 shadow-primary/30;
                }
                .fc-timegrid-now-indicator-line { border-color: #fe7b12 !important; border-width: 2px !important; }
                .fc-external-event:hover { transform: translateX(5px); }
                .fc-day-today { background-color: rgba(139, 92, 246, 0.05) !important; }

                /* Premium Cinematic Tippy Theme */
                .tippy-box[data-theme~='glass-premium'] {
                    background: linear-gradient(135deg, rgba(30, 20, 60, 0.96) 0%, rgba(15, 25, 55, 0.96) 100%) !important;
                    @apply !backdrop-blur-2xl !border !border-white/10 !rounded-3xl !shadow-2xl overflow-hidden;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 30px rgba(139, 92, 246, 0.15) !important;
                }
                .tippy-box[data-theme~='glass-premium']::after {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(to bottom right, rgba(255,255,255,0.05), transparent 40%);
                    pointer-events: none;
                }
                .tippy-box[data-theme~='glass-premium'] .tippy-content {
                    @apply !p-0;
                }
                .tippy-box[data-theme~='glass-premium'] .tippy-arrow {
                    @apply !text-slate-900/90;
                }

                @keyframes gradient-x {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }
                .animate-gradient-x {
                    background-size: 200% 200%;
                    animation: gradient-x 3s ease infinite;
                }
            `}} />
        </div>
    );
};

export default Agenda;
